import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  REPORTS_BASE_URL,
  DETAIL_CATEGORY_SALE_REPORT_FACT_API,
} from "../../../Constants/Config";

const initialState = {
  loading: false,
  loadingMore: false,
  detailCategorySaleData: [],
  // Pagination is by CATEGORY: each page brings the next N category tables.
  page: 1,
  hasMore: false,
  totalCategories: 0,
  // Server-side sums over EVERY category, so the Grand Total row is right
  // before the last page has been scrolled into view.
  grandTotals: null,
  successMessage: "",
  status: false,
  error: "",
};
let cancelTokenSource;

// One request builder for both the paged screen fetch and the CSV export's
// full fetch (no page_size), so the two always send the same filters.
export const requestDetailCategorySale = (data, cancelToken) => {
  const { token, append, ...newData } = data;
  return axios.post(
    REPORTS_BASE_URL + DETAIL_CATEGORY_SALE_REPORT_FACT_API,
    {
      ...newData,
      // The Outlet picker emits `stores` as a comma-joined merchant_id
      // list; that IS the multi-location filter. Fall back to the
      // logged-in merchant so a single-store login stays scoped rather
      // than widening to every outlet.
      outlet_ids:
        newData?.outlet_ids || newData?.stores || newData?.merchant_id,
    },
    {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
      cancelToken,
    }
  );
};
// Generate pening , fulfilled and rejected action type

export const fetchdetailCategorySaleData = createAsyncThunk(
  "detailCategorySale/fetchdetailCategorySaleData",
  async (data, { rejectWithValue }) => {
    const { append } = data;

    if (cancelTokenSource) {
      cancelTokenSource.cancel("Previous request canceled");
    }

    // Create a new cancel token
    cancelTokenSource = axios.CancelToken.source();
    try {
      const response = await requestDetailCategorySale(
        data,
        cancelTokenSource.token
      );
      if (response.data.status === true) {
        const categoryList = Object.keys(response.data.report_data);
        const reportData = response.data.report_data;
        const status = response.data.status;
        const pagination = response.data.pagination || {};
        return {
          categoryList,
          reportData,
          status,
          append: Boolean(append),
          page: Number(pagination.page || 1),
          hasMore: Boolean(pagination.has_more),
          totalCategories: Number(
            pagination.total_categories || categoryList.length
          ),
          grandTotals: response.data.grand_totals || null,
        };
      } else if (
        response.data.status === false &&
        response.data.message === "No data found"
      ) {
        // If 'report_data' is null, return an empty array and object
        return {
          categoryList: [],
          reportData: {},
          status: false,
          append: Boolean(append),
          page: 1,
          hasMore: false,
          totalCategories: 0,
          grandTotals: null,
        };
      } else {
        // Handle other error scenarios here if needed
        throw new Error(response.data.message);
      }
    } catch (error) {
      // throw new Error(error.response.data.message);
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

// Generate pening , fulfilled and rejected action type

const detailCategorySaleSlice = createSlice({
  name: "detailCategorySale",
  initialState,
  reducers: {
    updateStateOfDetailCategory: (state) => {
      state.detailCategorySaleData = [];
      state.page = 1;
      state.hasMore = false;
      state.totalCategories = 0;
      state.grandTotals = null;
    },
    updateStateOfDetailCategoryStatus: (state) => {
      state.status = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchdetailCategorySaleData.pending, (state, action) => {
      if (action.meta.arg?.append) {
        state.loadingMore = true;
      } else {
        state.loading = true;
      }
    });
    builder.addCase(fetchdetailCategorySaleData.fulfilled, (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.loadingMore = false;
      if (payload.append) {
        // Next page: its categories go after the ones already shown. Keys
        // are category names and a category lives on exactly one page.
        state.detailCategorySaleData = {
          ...(state.detailCategorySaleData || {}),
          ...payload.reportData,
        };
        state.categoryList = [
          ...(state.categoryList || []),
          ...payload.categoryList,
        ];
      } else {
        state.detailCategorySaleData = payload.reportData;
        state.categoryList = payload.categoryList;
        state.status = payload.status;
      }
      state.page = payload.page;
      state.hasMore = payload.hasMore;
      state.totalCategories = payload.totalCategories;
      state.grandTotals = payload.grandTotals;
      state.error = "";
    });
    builder.addCase(fetchdetailCategorySaleData.rejected, (state, action) => {
      if (action.meta.arg?.append) {
        state.loadingMore = false;
        // Superseded by a newer search: that request owns the state now.
        if (action.payload?.status === "canceled") return;
        // A failed next page keeps what is already on screen; stop the
        // sentinel from retrying in a loop.
        state.hasMore = false;
        return;
      }
      if (action.payload?.status === "canceled") return;
      state.loading = false;
      state.loadingMore = false;
      state.detailCategorySaleData = {};
      state.error = action.error.message;
    });
  },
});

export const {
  updateStateOfDetailCategory,
  updateStateOfDetailCategoryStatus,
} = detailCategorySaleSlice.actions;
export default detailCategorySaleSlice.reducer;
